// ── Tenant Identity Bar ──
// Persistent, highly visible tenant context indicator.
// Shows current tenant, environment, and cross-tenant warnings.

import { AlertTriangle, Building2, Lock, Globe } from "lucide-react";
import { useTenant } from "@/data/tenancy/context";
import { Badge } from "@/components/ui/badge";

const ENV_LABELS: Record<string, { label: string; className: string }> = {
  production: { label: "Production", className: "bg-success/15 text-success border-success/30" },
  staging: { label: "Staging", className: "bg-warning/15 text-warning border-warning/30" },
  pilot: { label: "Pilot", className: "bg-primary/15 text-primary border-primary/30" },
  internal: { label: "Internal", className: "bg-accent text-accent-foreground border-accent" },
  dev: { label: "Dev", className: "bg-muted text-muted-foreground border-border" },
};

export function TenantIdentityBar() {
  // Pilot mode: single-tenant deployments only — hide tenant chrome entirely.
  // Re-enable by removing this early return when multi-tenant rollout begins.
  return null;
   
  const { activeTenantId, activeTenant, canSwitchTenant } = useTenant();

  const isCrossTenant = canSwitchTenant && !activeTenantId;
  const env = activeTenant?.environment ?? "internal";
  const envMeta = ENV_LABELS[env] ?? ENV_LABELS.dev;

  // Cross-tenant warning bar
  if (isCrossTenant) {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 bg-warning/10 border-b border-warning/30 text-warning text-xs font-medium">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <Globe className="h-3 w-3 shrink-0" />
        <span className="font-semibold">All Companies View</span>
        <span className="text-warning/60 mx-1">·</span>
        <span className="text-warning/70">Cross-tenant mode — destructive actions require confirmation</span>
        <Badge variant="outline" className="ml-auto border-warning/40 text-warning text-[10px] px-1.5 py-0">
          ⚠ Cross-Tenant
        </Badge>
      </div>
    );
  }

  // Single tenant context bar
  return (
    <div className="flex items-center gap-2 px-4 py-1.5 bg-card/50 border-b border-border text-xs">
      <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="font-semibold text-foreground">{activeTenant?.name ?? "Unknown"}</span>
      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border ${envMeta.className}`}>
        {envMeta.label}
      </Badge>
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 border border-primary/30 bg-primary/10 text-primary"
        title="Koredor is in active beta — your feedback shapes the product."
      >
        Beta
      </Badge>
      {!canSwitchTenant && (
        <Lock className="h-3 w-3 text-muted-foreground/50 ml-1" />
      )}
      <span className="ml-auto text-muted-foreground/50 text-[10px]">
        Tenant: {activeTenant?.slug ?? activeTenantId}
      </span>
    </div>
  );
}
